<template>
    <a-table
        class="cot-table"
        size="small"
        :columns="columns"
        :data-source="offers"
        :pagination="false"
        row-key="idVendedor"
    >
        <template slot="winnerCheck" slot-scope="text, winner">
            <a-tooltip
                :title="winner.qtdPedido > 0 ? 'Fornecedor com pedido gerado' : ''"
            >
                <a-checkbox
                    :checked="isWinner(winner)"
                    :disabled="winner.is_loading || winner.qtdPedido > 0"
                    @click.stop="onToggleWinner(winner)"
                />
            </a-tooltip>
        </template>
        <template slot="fornecedor" slot-scope="text, winner">
            <div class="cot-sup">
                <div class="cot-nrow">
                    <span class="cot-n">{{ winner.nomeFornecedor }}</span>
                    <span v-if="isWinner(winner)" class="cot-tag win">Vencedor</span>
                    <span v-if="bestPrice === winner.valor" class="cot-tag best">Menor preço</span>
                </div>
                <span class="cot-v" v-if="winner.nomeVendedor">{{ winner.nomeVendedor }}</span>
            </div>
        </template>
        <template slot="marca" slot-scope="text, winner">{{
            winner.pro_observacao || "—"
        }}</template>
        <template slot="forma" slot-scope="text, winner">
            {{ winner.forma || "-" }}
            <span
                v-if="showPaymentConditionAlert(winner)"
                class="cot-alert"
                title="Condição diferente da cotação"
                >!</span
            >
        </template>
        <template slot="dataPrevista" slot-scope="text, winner">{{
            winner.data_entrega | formatDateOnly
        }}</template>
        <template slot="qtdEmb" slot-scope="text, winner">
            {{
                record.flag_generico == 1
                    ? winner.embalagem
                    : record.qtd_embalagem
            }}
        </template>
        <template slot="valorUnit" slot-scope="text, winner">{{
            winner.valor | customCurrency
        }}</template>
        <template slot="deltaWinner" slot-scope="text, winner">
            <span
                v-if="deltaVsWinner(winner) != null"
                :class="deltaClass(deltaVsWinner(winner))"
            >
                {{
                    deltaVsWinner(winner) > 0
                        ? "▲"
                        : deltaVsWinner(winner) < 0
                        ? "▼"
                        : ""
                }}
                {{ Math.abs(deltaVsWinner(winner)).toFixed(1) }}%
            </span>
            <span v-else>—</span>
        </template>
        <template slot="qtdComprar" slot-scope="text, winner">
            <template v-if="!hasMultipleStores">
                <a-input
                    v-if="winner.qtdPedido > 0"
                    class="cot-qty-input"
                    :value="winner.quantidade || 0"
                    disabled
                />
                <a-input
                    v-else-if="activeSeller === winner.idVendedor"
                    ref="activeInput"
                    class="cot-qty-input"
                    v-model="activeValue"
                    @input="handleQuantityInput"
                    @blur="commitQuantity(winner)"
                    @keydown="handleQuantityKeydown($event, winner)"
                />
                <span
                    v-else
                    class="cot-qty-input ant-input cot-qty-display"
                    @click="activateQuantity(winner)"
                >{{ winner.quantidade || 0 }}</span>
            </template>
            <span class="cot-qlojas-total" v-else>
                {{ winner.quantidade || 0 }}
            </span>
        </template>
        <template slot="faturamento" slot-scope="text, winner">
            <div class="cot-billing" v-if="billing && billing[winner.idVendedor]">
                <span :class="billingClass(billing[winner.idVendedor])">
                    Atual {{ billing[winner.idVendedor].current | customCurrency }}
                </span>
                <span class="cot-billing-min">
                    Mín.
                    <template v-if="billing[winner.idVendedor].minimum">{{ billing[winner.idVendedor].minimum | customCurrency }}</template>
                    <template v-else>—</template>
                </span>
            </div>
            <span v-else>—</span>
        </template>
        <template slot="observacao" slot-scope="text, winner">
            <span
                v-if="winner.observacao"
                class="cot-obs"
                :title="winner.observacao"
                >{{ formatObs(winner.observacao) }}</span
            >
            <span v-else>—</span>
        </template>
        <template slot="acoes" slot-scope="text, winner">
            <slot name="acoes" :winner="winner"></slot>
        </template>
    </a-table>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import currencyPrecision from "@/mixins/currencyPrecision";
import { loaderMixin } from "@/mixins/sweet-loader.mixin";

export default {
    name: "OffersTable",
    mixins: [currencyPrecision, loaderMixin],
    props: {
        record: { type: Object, required: true },
        offers: { type: Array, default: () => [] },
        hasMultipleStores: { type: Boolean, default: false },
        showActions: { type: Boolean, default: true },
        billing: { type: Object, default: null },
        quotationId: [String, Number],
        storeId: [String, Number],
    },
    data() {
        return {
            svc: new QuotationService(),
            activeSeller: null,
            activeValue: "",
        };
    },
    computed: {
        bestPrice() {
            const values = this.offers
                .map((v) => v.valor)
                .filter((v) => v != null);
            return values.length ? Math.min(...values) : null;
        },
        columns() {
            const cols = [
                {
                    title: "",
                    key: "check",
                    width: 40,
                    scopedSlots: { customRender: "winnerCheck" },
                },
                {
                    title: "Fornecedor",
                    key: "fornecedor",
                    scopedSlots: { customRender: "fornecedor" },
                },
                {
                    title: "Marca",
                    key: "marca",
                    scopedSlots: { customRender: "marca" },
                },
                {
                    title: "Forma de Pgto",
                    key: "forma",
                    scopedSlots: { customRender: "forma" },
                },
                {
                    title: "Data Prevista",
                    key: "dataPrevista",
                    scopedSlots: { customRender: "dataPrevista" },
                },
                {
                    title: "Qtd. Emb.",
                    key: "qtdEmb",
                    align: "right",
                    scopedSlots: { customRender: "qtdEmb" },
                },
                {
                    title: "Valor unitário",
                    key: "valorUnit",
                    align: "right",
                    scopedSlots: { customRender: "valorUnit" },
                },
                {
                    title: "vs. última",
                    key: "deltaWinner",
                    align: "right",
                    scopedSlots: { customRender: "deltaWinner" },
                },
                {
                    title: "Qtd. a comprar",
                    key: "qtdComprar",
                    align: "right",
                    scopedSlots: { customRender: "qtdComprar" },
                },
                {
                    title: "Observação",
                    key: "observacao",
                    scopedSlots: { customRender: "observacao" },
                },
            ];

            if (this.billing) {
                cols.splice(cols.length - 1, 0, {
                    title: "Faturamento",
                    key: "faturamento",
                    align: "right",
                    scopedSlots: { customRender: "faturamento" },
                });
            }

            if (this.showActions) {
                cols.push({
                    title: "Ações",
                    key: "acoes",
                    width: 60,
                    scopedSlots: { customRender: "acoes" },
                });
            }

            return cols;
        },
    },
    methods: {
        isWinner(winner) {
            if (winner.quantidade == 0 && winner.vencedor_quantidade == 0)
                return false;
            return winner.vencedor_quantidade > 0;
        },
        deltaVsWinner(winner) {
            if (!this.record.last_purchase || !this.record.last_purchase.valor)
                return null;
            return (
                ((winner.valor - this.record.last_purchase.valor) /
                    this.record.last_purchase.valor) *
                100
            );
        },
        deltaClass(delta) {
            if (delta > 0) return "up";
            if (delta < 0) return "down";
            return "flat";
        },
        billingClass({ current, minimum }) {
            if (!minimum || !current) return "";
            return current >= minimum ? "down" : "up";
        },
        formatObs(text) {
            if (!text) return "";
            return text.length > 25 ? text.slice(0, 24) + "..." : text;
        },
        showPaymentConditionAlert(winner) {
            const apiCondition = this.record.cot_condicao_pagamento;
            if (!winner.forma || apiCondition == null || apiCondition === "")
                return false;
            return (
                winner.forma.toLowerCase().trim() !==
                apiCondition.toLowerCase().trim()
            );
        },
        onToggleWinner(winner) {
            if (winner.qtdPedido > 0) {
                this.snack(
                    "Não é possível alterar: este fornecedor já possui pedido gerado.",
                    "error"
                );
                return;
            }
            this.$emit("toggle-winner", { winner, product: this.record });
        },
        editableOffers() {
            return this.offers.filter((o) => !(o.qtdPedido > 0));
        },
        activateQuantity(winner) {
            if (this.hasMultipleStores || winner.qtdPedido > 0) return;

            this.activeValue = String(winner.quantidade || "");
            this.activeSeller = winner.idVendedor;

            this.$nextTick(() => {
                const refs = this.$refs.activeInput;
                const el = Array.isArray(refs) ? refs[0] : refs;

                if (el) {
                    el.focus();
                    el.select();
                }
            });
        },
        activateEdge(direction) {
            const rows = this.editableOffers();
            const target = direction > 0 ? rows[0] : rows[rows.length - 1];

            if (!target) return false;

            this.activateQuantity(target);
            return true;
        },
        handleQuantityInput(event) {
            let value = event.target.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "");

            if (+value > 999999) value = "999999";

            this.activeValue = value;
            event.target.value = value;
        },
        commitQuantity(winner) {
            if (this.activeSeller !== winner.idVendedor) return;

            this.activeSeller = null;
            this.saveQuantity(winner, Number(this.activeValue) || 0);
        },
        handleQuantityKeydown(event, winner) {
            if (!["Enter", "ArrowDown", "ArrowUp"].includes(event.key)) return;

            event.preventDefault();

            const rows = this.editableOffers();
            const index = rows.indexOf(winner);
            const step = event.key === "ArrowUp" ? -1 : 1;
            const next = rows[index + step];

            event.target.blur();

            if (next) {
                this.activateQuantity(next);
                return;
            }

            this.$emit("navigate-out", step);
        },
        saveQuantity(winner, quantidade) {
            if (winner.quantidade === quantidade) return;
            const previous = winner.quantidade;
            this.$set(winner, "quantidade", quantidade);
            this.svc
                .sendQuantidade({
                    numerocotacao: this.quotationId,
                    quantidade: quantidade || "0",
                    vendedor: winner.idVendedor,
                    produto: winner.pro_id,
                    loja: this.storeId,
                })
                .catch((error) => {
                    this.$set(winner, "quantidade", previous);
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.cot-table {
    background: #fff;
}
.cot-sup {
    display: flex;
    flex-direction: column;
    gap: 3px;
}
.cot-nrow {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.cot-n {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
}
.cot-v {
    font-size: 12px;
    color: #8c8c8c;
    font-style: italic;
}
.cot-obs {
    color: #cf1322;
    font-weight: 600;
}
.cot-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    padding: 1px 8px;
    border-radius: 4px;
    line-height: 18px;
    white-space: nowrap;

    &.win {
        background: var(--primary-color);
        color: #fff;
    }
    &.best {
        background: #f6ffed;
        color: #389e0d;
        border: 1px solid #b7eb8f;
    }
}
.cot-alert {
    display: inline-block;
    width: 16px;
    height: 16px;
    line-height: 16px;
    text-align: center;
    border-radius: 50%;
    background: var(--primary-color);
    color: #fff;
    font-weight: bold;
    font-size: 11px;
    margin-left: 4px;
}
.up {
    color: #cf1322;
}
.down {
    color: #389e0d;
}
.flat {
    color: #8c8c8c;
}

.cot-billing {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 600;
}
.cot-billing-min {
    font-size: 12px;
    font-weight: 400;
    color: #8c8c8c;
}

.cot-qty-input {
    width: 64px;
    text-align: center;

    ::v-deep(.ant-input) {
        text-align: center;
    }
}
.cot-qty-display {
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: text;
}
.cot-qlojas-total {
    font-size: 13px;
    font-weight: 600;
    color: #262626;
}
</style>
